import { Button, Form, Select, Upload, Table, Spin, Typography } from "antd";
import {
  UploadOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useGeoLiftForm } from "../../../../hooks/useGeoLiftForm";
import CustomExcelIcon from "../../../../assets/icons/CustomExcelIcon";

const { Text } = Typography;

interface PostTestControlMarketSelectionProps {
  jobConfigId: string;
  setPostTestData: React.Dispatch<React.SetStateAction<any>>;
  onNext: () => void;
}

const PostTestControlMarketSelection: React.FC<
  PostTestControlMarketSelectionProps
> = ({ jobConfigId, setPostTestData, onNext }) => {
  const {
    form,
    fileList,
    loading,
    csvData,
    testMarkets,
    columns,
    syntheticMarketOptions,
    showSyntheticField,
    uploadProps,
    onFinish,
    handleTestMarketChange,
    setCsvData,
    setFileList,
  } = useGeoLiftForm(jobConfigId, setPostTestData, onNext);

  return (
    <div className="mt-10">
      <div className="bg-[var(--blue-subtle)] p-4 rounded-lg border border-[var(--border-subtle)] mb-6">
        <div className="flex items-start">
          <CustomExcelIcon className="text-[var(--blue)] text-xl mt-1 mr-3" />
          <div>
            <Text strong className="text-[var(--blue)] block mb-1">
              Data File Requirements
            </Text>
            <Text className="text-[var(--text-secondary)] block text-sm">
              • The uploaded file should contain conversion data for the time
              after the campaign started. .<br />• Accepted format:{" "}
              <strong>CSV</strong> only, with column headers in the first row.
            </Text>
            <Button
              type="link"
              className="text-[var(--blue)] p-0 mt-2 flex items-start justify-start"
              href="/sample-geo-lift-post.scsv"
              download
            >
              <CustomExcelIcon className="mr-1" /> Download Sample CSV
            </Button>
          </div>
        </div>
      </div>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <Spin size="large" />
          </div>
        ) : (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-6">
              <Form.Item
                label="Upload Data"
                name="postTestData"
                rules={[{ required: true, message: "Please upload CSV file!" }]}
                className="mb-0 w-full [&_.ant-upload]:w-full [&_.ant-upload-select]:w-full"
              >
                <div className="space-y-2 w-full">
                  <Upload {...uploadProps} className="w-full">
                    <Button icon={<UploadOutlined />} className="w-full">
                      Click to upload CSV
                    </Button>
                  </Upload>
                  <div className="text-sm text-[var(--text-secondary)]">
                    <a
                      href="/sample-post-test.csv"
                      download
                      className="text-[var(--blue)] hover:underline"
                    >
                      Download Sample CSV
                    </a>
                  </div>
                </div>
              </Form.Item>
              <Form.Item
                label="Select Test Markets to Produce Lift Results"
                name="testMarket"
                rules={[
                  { required: true, message: "Please select a test market!" },
                ]}
                className="mb-0 w-full"
              >
                <Select
                  placeholder="Select a test market..."
                  options={testMarkets.map((location) => ({
                    label: location,
                    value: location,
                  }))}
                  className="w-full"
                  onChange={handleTestMarketChange}
                />
              </Form.Item>
              {showSyntheticField && (
                <Form.Item
                  name="syntheticMarket"
                  initialValue={syntheticMarketOptions?.[0]?.label}
                  label="Best Synthetic Control Market"
                  className="mb-0 md:col-span-2 w-full"
                >
                  {syntheticMarketOptions &&
                  syntheticMarketOptions.length > 0 ? (
                    <div className="bg-[var(--bg-surface)] p-4 rounded-lg border border-[var(--border-default)]">
                      <div className="flex items-start">
                        <div className="flex-1">
                          <div className="text-sm text-[var(--text-secondary)] mb-1">
                            Best Synthetic Control Market:
                          </div>
                          <div className="text-base font-medium text-[var(--text-primary)]">
                            {syntheticMarketOptions[0].label
                              .split(", ")
                              .map((location) => {
                                const city = location.split(" - ")[0];
                                return city
                                  .split(" ")
                                  .map(
                                    (word) =>
                                      word.charAt(0).toUpperCase() +
                                      word.slice(1)
                                  )
                                  .join(" ");
                              })
                              .join(", ")}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Text type="secondary">
                      Select Test market, we will identify best control market
                      for you!
                    </Text>
                  )}
                </Form.Item>
              )}
            </div>

            {csvData.length > 0 && (
              <div className="w-full mt-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-semibold">CSV Data Preview</h3>
                  <Button
                    type="default"
                    onClick={() => {
                      setCsvData([]);
                      setFileList([]);
                    }}
                    icon={<CloseOutlined />}
                  >
                    Clear Preview
                  </Button>
                </div>
                <Table
                  columns={columns}
                  dataSource={csvData.slice(0, 10)}
                  pagination={false}
                  scroll={{ x: true }}
                  bordered
                  size="middle"
                  className="w-full shadow-sm"
                />
              </div>
            )}

            <Form.Item className="w-full flex justify-end mt-8">
              <Button
                type="primary"
                htmlType="submit"
                className="w-full py-4 text-lg"
              >
                Generate Analysis
              </Button>
            </Form.Item>
          </div>
        )}
      </Form>
    </div>
  );
};

export default PostTestControlMarketSelection;
